import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

const formVide = {
  nom: "", prenom: "", sexe: "", date_naissance: "",
  num_passeport: "", date_expiration_passeport: "",
  telephone: "", email: "", nationalite: "", montant: "", tarif_id: "",
};

function OumrahAccompagnateurs() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [accompagnateurs, setAccompagnateurs] = useState([]);
  const [tarifs, setTarifs] = useState([]);
  const [form, setForm] = useState({ ...formVide });
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get("/oumrah/sessions").then((res) => setSessions(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (sessionId) {
      chargerAccompagnateurs(sessionId);
      api.get("/oumrah/sessions/" + sessionId).then((res) => setTarifs(res.data.tarifs || [])).catch(() => setTarifs([]));
    } else {
      setAccompagnateurs([]);
      setTarifs([]);
    }
  }, [sessionId]);

  const chargerAccompagnateurs = async (sid) => {
    try {
      const res = await api.get("/oumrah/sessions/" + sid + "/accompagnateurs");
      setAccompagnateurs(res.data.accompagnateurs || []);
    } catch (err) {
      setAccompagnateurs([]);
    }
  };

  const maj = (champ, valeur) => setForm({ ...form, [champ]: valeur });

  const creer = async () => {
    if (!sessionId) { alert("Choisissez d'abord une session"); return; }
    if (!form.nom.trim() || !form.prenom.trim()) { alert("Nom et prénom obligatoires"); return; }
    setLoading(true);
    const payload = {
      ...form,
      date_naissance: form.date_naissance || null,
      date_expiration_passeport: form.date_expiration_passeport || null,
    };
    try {
      if (editId) {
        await api.put("/oumrah/sessions/accompagnateurs/" + editId, payload);
      } else {
        await api.post("/oumrah/sessions/" + sessionId + "/accompagnateurs", payload);
      }
      setForm({ ...formVide });
      setEditId(null);
      chargerAccompagnateurs(sessionId);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };
  const editer = (a) => {
    setEditId(a.id);
    setForm({
      nom: a.nom || "", prenom: a.prenom || "", sexe: a.sexe || "",
      date_naissance: a.date_naissance ? a.date_naissance.slice(0, 10) : "",
      num_passeport: a.num_passeport || "",
      date_expiration_passeport: a.date_expiration_passeport ? a.date_expiration_passeport.slice(0, 10) : "",
      telephone: a.telephone || "", email: a.email || "",
      nationalite: a.nationalite || "", montant: a.cout_reel != null ? a.cout_reel : "", tarif_id: a.tarif_id || "",
    });
  };
  const annulerEdition = () => { setEditId(null); setForm({ ...formVide }); };

  const supprimer = async (id) => {
    if (!window.confirm("Supprimer cet accompagnateur ? Sa dépense dans le bilan sera aussi supprimée.")) return;
    try {
      await api.delete("/oumrah/sessions/accompagnateurs/" + id);
      chargerAccompagnateurs(sessionId);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const fmt = (n) => parseFloat(n || 0).toLocaleString("fr-FR");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h5" fontWeight="bold" mb={1}>Accompagnateurs</MDTypography>
          <MDTypography variant="caption" color="text" mb={2} display="block">
            Les accompagnateurs ne paient pas et ne comptent pas dans les quotas. Leur coût réel est ajouté au bilan de la session.
          </MDTypography>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="sess-label">Session</InputLabel>
            <Select labelId="sess-label" label="Session" sx={{ height: 44 }}
              value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
              <MenuItem value="">— Choisir —</MenuItem>
              {sessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.libelle}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Card>

        {sessionId && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Card sx={{ p: 3 }}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>{editId ? "Modifier l'accompagnateur" : "Nouvel accompagnateur"}</MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField label="Prénom" fullWidth size="small" value={form.prenom} onChange={(e) => maj("prenom", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Nom" fullWidth size="small" value={form.nom} onChange={(e) => maj("nom", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="sexe-label">Sexe</InputLabel>
                      <Select labelId="sexe-label" label="Sexe" sx={{ height: 44 }} value={form.sexe} onChange={(e) => maj("sexe", e.target.value)}>
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="H">Homme</MenuItem>
                        <MenuItem value="F">Femme</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Date de naissance" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.date_naissance} onChange={(e) => maj("date_naissance", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="N° passeport" fullWidth size="small" value={form.num_passeport} onChange={(e) => maj("num_passeport", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Expiration passeport" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.date_expiration_passeport} onChange={(e) => maj("date_expiration_passeport", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Téléphone" fullWidth size="small" value={form.telephone} onChange={(e) => maj("telephone", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Email" fullWidth size="small" value={form.email} onChange={(e) => maj("email", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Nationalité" fullWidth size="small" value={form.nationalite} onChange={(e) => maj("nationalite", e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="tarif-label">Ville de départ / Chambre</InputLabel>
                      <Select labelId="tarif-label" label="Ville de départ / Chambre" sx={{ height: 44 }}
                        value={form.tarif_id} onChange={(e) => maj("tarif_id", e.target.value)}>
                        <MenuItem value="">— Aucune —</MenuItem>
                        {tarifs.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {(t.ville_depart || "Sans ville") + " · " + (t.libelle || t.type_chambre || "")}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Coût réel (€)" type="number" fullWidth size="small" value={form.montant} onChange={(e) => maj("montant", e.target.value)} />
                  </Grid>
                </Grid>
                <MDBox display="flex" gap={1} mt={2}>
                  <MDButton color="info" fullWidth onClick={creer} disabled={loading}>
                    <Icon>{editId ? "save" : "add"}</Icon>&nbsp;{loading ? "..." : (editId ? "Enregistrer" : "Ajouter l'accompagnateur")}
                  </MDButton>
                  {editId && (
                    <MDButton color="secondary" variant="outlined" onClick={annulerEdition} disabled={loading}>
                      Annuler
                    </MDButton>
                  )}
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card sx={{ p: 3 }}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  {accompagnateurs.length} accompagnateur{accompagnateurs.length > 1 ? "s" : ""}
                </MDTypography>
                {accompagnateurs.length === 0 && (
                  <MDTypography variant="caption" color="text">Aucun accompagnateur pour cette session.</MDTypography>
                )}
                {accompagnateurs.map((a) => (
                  <MDBox key={a.id} display="flex" justifyContent="space-between" alignItems="center" py={1} sx={{ borderBottom: "1px solid #eee" }}>
                    <MDBox>
                      <MDTypography variant="button" fontWeight="bold">
                        <Icon sx={{ verticalAlign: "middle", fontSize: "1rem !important", color: a.sexe === "H" ? "#1565c0" : "#c2185b" }}>
                          {a.sexe === "H" ? "man" : "woman"}
                        </Icon>
                        &nbsp;{a.prenom} {a.nom}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {a.telephone || "—"} · Coût réel : <b>{fmt(a.cout_reel)} €</b>
                        {a.chambre_id ? " · Placé en chambre" : " · Non placé"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <IconButton size="small" onClick={() => editer(a)} title="Modifier">
                        <Icon fontSize="small" sx={{ color: "#378ADD" }}>edit</Icon>
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => supprimer(a.id)} title="Supprimer">
                        <Icon fontSize="small">delete</Icon>
                      </IconButton>
                    </MDBox>
                  </MDBox>
                ))}
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OumrahAccompagnateurs;
